import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// Manuale HACCP — Zod schemas (T-060)
//
// Mantieni sincronizzato con:
//   - supabase/migrations/202605221700*.sql (schema DB)
//   - supabase/functions/_shared/manual-types.ts (mirror Deno-friendly)
//
// ────────────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────────
// Sub-schemas per la cucitura
// ────────────────────────────────────────────────────────────────────────────

const ContrattoEsternoSchema = z.object({
  azienda: z.string().min(1).max(200),
  p_iva: z
    .string()
    .regex(/^\d{11}$/)
    .optional(),
  frequenza: z.string().min(1).max(60),
  contratto_storage_path: z.string().min(1).optional(),
  mappa_esche_storage_path: z.string().min(1).optional(),
});

const AnalisiAcquaSchema = z.object({
  fonte: z.enum(['acquedotto', 'pozzo']),
  ultima_analisi_data: z.string().date().optional(),
  risultato_storage_path: z.string().min(1).optional(),
});

const CertificatoTaraturaSchema = z.object({
  strumento: z.string().min(1).max(200),
  data_taratura: z.string().date(),
  prossima_scadenza: z.string().date(),
  certificato_storage_path: z.string().min(1).optional(),
});

// ────────────────────────────────────────────────────────────────────────────
// Schema riga manual_tenant_inputs (cucitura per tenant)
// ────────────────────────────────────────────────────────────────────────────

export const ManualTenantInputsSchema = z.object({
  tenant_id: z.string().uuid(),
  categoria_slug: z.string().min(1).default('ristorazione-bar'),
  config_overrides: z.record(z.any()).default({}),
  descrizione_locale: z.string().max(5000).optional(),
  planimetria_storage_path: z.string().optional(),
  contratto_disinfestazione: ContrattoEsternoSchema.optional(),
  contratto_rifiuti: ContrattoEsternoSchema.optional(),
  contratto_manutenzione: ContrattoEsternoSchema.optional(),
  analisi_acqua: AnalisiAcquaSchema.optional(),
  certificati_taratura: z.array(CertificatoTaraturaSchema).default([]),
  note_per_sezione: z.record(z.string()).default({}),
  updated_at: z.string().datetime().optional(),
  updated_by_user_id: z.string().uuid().optional(),
});

export type ManualTenantInputs = z.infer<typeof ManualTenantInputsSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Schema context passato ai template Handlebars
// ────────────────────────────────────────────────────────────────────────────

const TenantContextSchema = z.object({
  id: z.string().uuid(),
  denominazione: z.string().min(1),
  p_iva: z
    .string()
    .regex(/^\d{11}$/)
    .optional(),
  sede_legale: z
    .object({
      via: z.string().optional(),
      citta: z.string().optional(),
      cap: z.string().optional(),
      provincia: z.string().optional(),
    })
    .optional(),
  sede_operativa: z
    .object({
      via: z.string().optional(),
      citta: z.string().optional(),
      cap: z.string().optional(),
      provincia: z.string().optional(),
    })
    .optional(),
  rea_number: z.string().optional(),
  forma_giuridica: z.string().optional(),
  scia_protocol: z.string().optional(),
  scia_date: z.string().date().optional(),
  notifica_sanitaria_ente: z.string().optional(),
  notifica_sanitaria_date: z.string().date().optional(),
  responsabile_haccp_name: z.string().optional(),
  manuale_haccp_version: z.string().optional(),
  manuale_haccp_revised_at: z.string().date().optional(),
});

const BusinessContextSchema = z.object({
  tipologia_attivita: z.string().optional(),
  posti_sala: z.number().int().nonnegative().optional(),
  posti_dehor: z.number().int().nonnegative().optional(),
});

const OrganigrammaRowSchema = z.object({
  nome: z.string().min(1),
  ruolo: z.string().min(1),
  categoria_rischio_dA698_2022: z.enum(['A', 'B', 'C']).nullable().optional(),
  attestato_url: z.string().url().optional(),
  scadenza_attestato: z.string().date().optional(),
});

const FornitoreRowSchema = z.object({
  ragione_sociale: z.string().min(1),
  p_iva: z.string().optional(),
  categoria_merceologica: z.string().optional(),
  scheda_tecnica_url: z.string().url().optional(),
});

// T-459 — Riga scheda tecnica di un articolo (prodotto/materia prima o ricetta)
// con PDF caricato sul bucket privato `tech-sheets`. Popolata da manual-build
// solo con articoli del tenant corrente (isolamento via tenant_id).
const ArticoloSchedaTecnicaRowSchema = z.object({
  nome: z.string().min(1),
  tipo: z.enum(['prodotto', 'ricetta']),
  categoria: z.string().optional(),
  scheda_tecnica_url: z.string().url().optional(),
});

const DetergenteRowSchema = z.object({
  nome: z.string().min(1),
  principio_attivo: z.string().optional(),
  scheda_sicurezza_url: z.string().url().optional(),
  frequenza_uso: z.string().optional(),
});

const CucituraContextSchema = z.object({
  descrizione_locale: z.string().optional(),
  planimetria_url: z.string().url().optional(),
  organigramma: z.array(OrganigrammaRowSchema),
  fornitori_qualificati: z.array(FornitoreRowSchema),
  // T-459 — Schede tecniche di prodotti e ricette con PDF caricato (par. 2.1.4).
  schede_tecniche_articoli: z.array(ArticoloSchedaTecnicaRowSchema).default([]),
  detergenti_in_uso: z.array(DetergenteRowSchema),
  contratto_disinfestazione: z
    .object({
      azienda: z.string(),
      p_iva: z.string().optional(),
      frequenza: z.string(),
      contratto_url: z.string().url().optional(),
      mappa_esche_url: z.string().url().optional(),
    })
    .optional(),
  contratto_rifiuti: z
    .object({
      azienda: z.string(),
      p_iva: z.string().optional(),
      frequenza: z.string(),
      contratto_url: z.string().url().optional(),
    })
    .optional(),
  contratto_manutenzione: z
    .object({
      azienda: z.string(),
      p_iva: z.string().optional(),
      frequenza: z.string(),
      contratto_url: z.string().url().optional(),
    })
    .optional(),
  analisi_acqua: z
    .object({
      fonte: z.enum(['acquedotto', 'pozzo']),
      ultima_analisi_data: z.string().date().optional(),
      risultato_url: z.string().url().optional(),
    })
    .optional(),
  certificati_taratura: z.array(
    z.object({
      strumento: z.string(),
      data_taratura: z.string().date(),
      prossima_scadenza: z.string().date(),
      certificato_url: z.string().url().optional(),
    }),
  ),
  note_per_sezione: z.record(z.string()),
});

const ConfigSchema = z.object({
  categoria_slug: z.string().min(1),
  categoria_nome: z.string().min(1),
  has_pesce_crudo: z.boolean(),
  has_bar: z.boolean(),
  has_buffet_colazione: z.boolean(),
  has_dehor: z.boolean(),
  has_pozzo_privato: z.boolean(),
  ccp_personalizzati: z.array(z.unknown()),
});

const RevisioneSchema = z.object({
  numero: z.string().regex(/^\d+\.\d+$/),
  data_emissione: z.string().date(),
  consulente_nome: z.string().min(1),
  consulente_qualifica: z.string().min(1),
});

export const ManualContextSchema = z.object({
  tenant: TenantContextSchema,
  business: BusinessContextSchema,
  cucitura: CucituraContextSchema,
  config: ConfigSchema,
  revisione: RevisioneSchema,
});

export type ManualContext = z.infer<typeof ManualContextSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Schema riga manual_revisions
// ────────────────────────────────────────────────────────────────────────────

export const ManualRevisionSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  revision_number: z.string().regex(/^\d+\.\d+$/),
  categoria_slug: z.string().min(1),
  status: z.enum(['draft', 'signed']),
  inputs_snapshot: z.record(z.unknown()),
  pdf_storage_path: z.string().min(1),
  pdf_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  sign_ready: z.boolean(),
  sign_check: z.record(z.unknown()),
  generated_by_user_id: z.string().uuid().optional(),
  generated_at: z.string().datetime(),
  signed_at: z.string().datetime().optional(),
  signed_by_user_id: z.string().uuid().optional(),
  signed_event_id: z.string().uuid().optional(),
});

export type ManualRevision = z.infer<typeof ManualRevisionSchema>;

// ────────────────────────────────────────────────────────────────────────────
// API contract types (request/response edge function manual-build)
// ────────────────────────────────────────────────────────────────────────────

export const ManualBuildRequestSchema = z.object({
  tenant_id: z.string().uuid(),
  mode: z.enum(['draft', 'sign']),
  revision_number: z
    .string()
    .regex(/^\d+\.\d+$/)
    .optional(),
}).superRefine((value, ctx) => {
  if (value.mode === 'sign' && !value.revision_number) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['revision_number'],
      message: 'revision_number is required in sign mode',
    });
  }
});
export type ManualBuildRequest = z.infer<typeof ManualBuildRequestSchema>;

export const ManualBuildResponseSchema = z.object({
  revision_id: z.string().uuid(),
  pdf_url: z.string().url(),
  pdf_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  status: z.enum(['draft', 'signed']),
  revision_number: z.string().regex(/^\d+\.\d+$/),
  missing_required_fields: z.array(z.string()),
  sign_ready: z.boolean(),
  missing_for_signing: z.array(z.object({
    field: z.string(),
    reason: z.string(),
  })),
});
export type ManualBuildResponse = z.infer<typeof ManualBuildResponseSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Rev. B — Schemi per i 3 nuovi moduli strutturati MOD 03 / 06 / 10
// Mantieni allineato con:
//   - supabase/migrations/20260522180050_manuale_haccp_non_conformities.sql
//   - supabase/migrations/20260522180100_manuale_haccp_equipment_maintenance.sql
//   - supabase/migrations/20260522180200_manuale_haccp_food_transports.sql
// ────────────────────────────────────────────────────────────────────────────

const NCFasiProcesso = [
  'acquisto',
  'trasporto_fornitore',
  'ricevimento',
  'stoccaggio_refrigerato',
  'stoccaggio_congelato',
  'stoccaggio_secco',
  'scongelamento',
  'pre_preparazione',
  'preparazione_fredda',
  'cottura',
  'abbattimento',
  'conservazione_cotti',
  'riscaldo',
  'somministrazione',
  'conservazione_avanzi',
  'confezionamento_trasporto',
  'trasporto',
  'consegna',
  'sanificazione',
  'manutenzione',
  'formazione',
  'altro',
] as const;

export const NonConformitySchema = z.object({
  id: z.string().uuid().optional(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid().optional().nullable(),
  rilevata_da_user_id: z.string().uuid().optional().nullable(),
  rilevata_at: z.string().datetime().optional(),
  fase_processo: z.enum(NCFasiProcesso),
  descrizione: z.string().min(5).max(5000),
  gravita: z.enum(['lieve', 'media', 'grave']),
  tipo_pericolo: z.enum(['B', 'C', 'F', 'A', 'altro']),
  prodotto_coinvolto: z.string().max(200).optional().nullable(),
  lotto: z.string().max(200).optional().nullable(),
  lotto_production_id: z.string().uuid().optional().nullable(),
  azione_correttiva: z.string().min(5).max(5000),
  azione_preventiva: z.string().max(5000).optional().nullable(),
  responsabile_azione: z.string().min(1).max(200),
  data_chiusura: z.string().date().optional().nullable(),
  esito: z.enum(['aperta', 'in_verifica', 'chiusa']).default('aperta'),
  verifica_efficacia: z.string().max(5000).optional().nullable(),
  foto_storage_path: z.string().optional().nullable(),
});

export type NonConformity = z.infer<typeof NonConformitySchema>;

const EMCategorieAttrezzatura = [
  'cottura',
  'refrigerazione',
  'lavaggio',
  'preparazione',
  'aspirazione',
  'bar',
  'colazione',
  'trasporto',
  'altro',
] as const;

const EMTipiIntervento = [
  'manutenzione_ordinaria',
  'riparazione',
  'sostituzione_pezzo',
  'sostituzione_completa',
  'taratura',
  'verifica_funzionale',
  'pulizia_profonda',
  'sanificazione_straordinaria',
  'altro',
] as const;

export const EquipmentMaintenanceSchema = z.object({
  id: z.string().uuid().optional(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid().optional().nullable(),
  attrezzatura: z.string().min(1).max(200),
  categoria_attrezzatura: z.enum(EMCategorieAttrezzatura).optional().nullable(),
  tipo_intervento: z.enum(EMTipiIntervento),
  data_intervento: z.string().date(),
  esecutore_interno: z.boolean().default(false),
  esecutore_nome: z.string().max(200).optional().nullable(),
  esecutore_ditta: z.string().max(200).optional().nullable(),
  esecutore_p_iva: z.string().max(20).optional().nullable(),
  descrizione: z.string().min(5).max(5000),
  pezzi_sostituiti: z.string().max(2000).optional().nullable(),
  esito: z.enum([
    'ok',
    'ok_con_riserva',
    'ko_attrezzatura_fuori_servizio',
    'rinviato',
  ]),
  prossima_scadenza: z.string().date().optional().nullable(),
  fattura_storage_path: z.string().optional().nullable(),
  certificato_storage_path: z.string().optional().nullable(),
  costo_eur: z.number().nonnegative().optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
});

export type EquipmentMaintenance = z.infer<typeof EquipmentMaintenanceSchema>;

const FTTipiAlimento = ['caldo', 'freddo', 'surgelato', 'ambiente'] as const;
const FTEsiti = [
  'ok',
  't_fuori_range',
  'contenitore_danneggiato',
  'ritardo',
  'rifiuto_destinatario',
  'altro',
] as const;

export const FoodTransportSchema = z.object({
  id: z.string().uuid().optional(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid().optional().nullable(),
  data_trasporto: z.string().datetime(),
  destinazione: z.string().min(1).max(300),
  cliente_finale: z.string().max(300).optional().nullable(),
  alimento: z.string().min(1).max(200),
  lotto_production_id: z.string().uuid().optional().nullable(),
  tipo_alimento: z.enum(FTTipiAlimento),
  qta: z.number().nonnegative().optional().nullable(),
  uom: z.string().max(20).optional().nullable(),
  contenitore_isotermico: z.string().max(200).optional().nullable(),
  t_partenza_c: z.number().optional().nullable(),
  t_arrivo_c: z.number().optional().nullable(),
  ora_partenza: z.string().datetime().optional().nullable(),
  ora_arrivo: z.string().datetime().optional().nullable(),
  operatore_user_id: z.string().uuid().optional().nullable(),
  operatore_nome: z.string().max(200).optional().nullable(),
  veicolo: z.string().max(200).optional().nullable(),
  esito: z.enum(FTEsiti).default('ok'),
  azione_correttiva: z.string().max(5000).optional().nullable(),
  documento_consegna_storage_path: z.string().optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
});

export type FoodTransport = z.infer<typeof FoodTransportSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Rev. B — Schema evento formativo (MOD 05) per staff_profiles.formazione_eventi
// ────────────────────────────────────────────────────────────────────────────

export const FormazioneEventoSchema = z.object({
  data: z.string().date(),
  argomento: z.string().min(1).max(500),
  docente: z.string().min(1).max(200),
  durata_ore: z.number().positive(),
  categoria_corso: z.enum(['base_12h', 'aggiornamento_6h', 'specifico', 'altro']).optional(),
  attestato_storage_path: z.string().optional(),
  note: z.string().max(2000).optional(),
});

export type FormazioneEvento = z.infer<typeof FormazioneEventoSchema>;
